import { useState } from "react";
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";
import { CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { useBankingValidation } from "@/features/users/useUsers";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const BankingValidationDialog = ({ 
  isOpen, 
  onClose, 
  user, 
  type // "approve" or "reject"
}) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const { approveBanking, rejectBanking, approveBankingLoading, rejectBankingLoading } = useBankingValidation();

  const isLoading = approveBankingLoading || rejectBankingLoading;
  const isApprove = type === "approve";

  const handleSubmit = () => {
    if (isApprove) {
      approveBanking(
        { id: user.id, notes },
        {
          onSuccess: () => {
            toast.success(t("banking.approvedSuccessfully"));
            onClose();
            setNotes("");
          },
          onError: (error) => {
            toast.error(error.response?.data?.message || t("messages.error"));
          },
        }
      );
    } else {
      if (!reason.trim()) {
        toast.error(t("banking.reasonRequired"));
        return;
      }
      rejectBanking(
        { id: user.id, reason: reason.trim(), notes: notes.trim() || null },
        {
          onSuccess: () => {
            toast.success(t("banking.rejectedSuccessfully"));
            onClose();
            setReason("");
            setNotes("");
          },
          onError: (error) => {
            toast.error(error.response?.data?.message || t("messages.error"));
          },
        }
      );
    }
  };

  const Icon = isApprove ? CheckCircle : XCircle;
  const iconColor = isApprove ? "text-green-600" : "text-red-600";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${iconColor}`} />
            {isApprove ? t("banking.approveBanking") : t("banking.rejectBanking")}
          </DialogTitle>
          <DialogDescription>
            {isApprove 
              ? t("banking.approveDescription", { name: user?.fullName })
              : t("banking.rejectDescription", { name: user?.fullName })
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {!isApprove && (
            <div className="space-y-2">
              <Label htmlFor="reason" className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                {t("banking.rejectionReason")} *
              </Label>
              <Textarea
                id="reason"
                placeholder={t("banking.rejectionReasonPlaceholder")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="notes">
              {t("banking.additionalNotes")} {isApprove ? "" : `(${t("common.optional")})`}
            </Label>
            <Textarea
              id="notes"
              placeholder={
                isApprove 
                  ? t("banking.approvalNotesPlaceholder")
                  : t("banking.additionalNotesPlaceholder")
              }
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {t("actions.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            variant={isApprove ? "default" : "destructive"}
          >
            {isLoading 
              ? t("main.loading") 
              : (isApprove ? t("banking.approve") : t("banking.reject"))
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BankingValidationDialog;